import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, FormControlLabel, Inline, Stack, Toggle } from '@airbus/components-react/components'
import UrlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import LangContext from 'context/LangContext'
import PageHeader from 'components/pageHeader'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { OrderDetail } from 'types/order'
import { NavItem } from 'types/navItems'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'
import { Message, MessageSeverity } from 'constants/message'
import { CustomNumericInput } from 'components/FormFields/FormNumericInput'
import { CustomInput } from 'components/FormFields/FormInput'
import History from './components/History/history'
import Refusal from './components/Refusal/refusal'
import Validation from './components/Validation/validation'
import MessageBlock from './components/messageBlock'

// No level defined for an order-type (CS, CA, CA-AOG, CR, or CL) = min. permission
const LEVEL_MIN_PERMISSION = 0

const ACTION_PERFORMED = { REFUSAL: 'REFUSAL', VALIDATION: 'VALIDATION' }

const OrderValidationPage = () => {
  const baseUrl = process.env.PUBLIC_URL
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { lang } = useContext(LangContext)
  const { cdnumdoc = null } = useParams()
  const [searchParams] = useSearchParams()
  const jumpToPage = Number(searchParams.get('page')) || 0

  const navItems: NavItem[] = [
    {
      title: t('9420008'),
      url: baseUrl + '/orders' + ((cdnumdoc && '?page=' + jumpToPage) || 0),
    },
    {
      title: cdnumdoc as string,
      url: baseUrl + '/orders/' + cdnumdoc,
    },
  ]

  const [order, setOrder] = useState<OrderDetail>()
  const { fetchData: fetchOrder } = useQuery<OrderDetail>(UrlApis.URL_ORDER_GET)

  // Validation level of logged user
  const [userNivVal, setUserNivVal] = useState(undefined)
  // Permissions
  const [canView, setCanView] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [isUserResponsible, setIsUserResponsible] = useState(false)
  // Optional message (info, warning) to display
  const [messageToDisplay, setMessageToDisplay] = useState<Message>()
  // Variables for the different states of the order
  const [isOrderSignedByResponsible, setIsOrderSignedByResponsible] = useState(false)
  const [isOrderFinalized, setIsOrderFinalized] = useState(false)
  const [canUserCompletlySign, setCanUserCompletlySign] = useState(false)

  const [urlPdf, setUrlPdf] = useState<string>()
  const [urlPdfKeyNum, setUrlPdfKeyNum] = useState(0)

  const [showHistory, setShowHistory] = useState(true)

  const canViewPrice = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.GLOBAL_PRICE_DSP_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  // State for the completion of the user action (when the user clicks on an action-button)
  const [isUserActionCompleted, setIsUserActionCompleted] = useState(false)

  const handleUserActionCompletion = (actionName: string) => {
    setIsUserActionCompleted(true)
    // Close modale in case of Refusal of order
    if (actionName === ACTION_PERFORMED.REFUSAL || actionName === ACTION_PERFORMED.VALIDATION) {
      navigate(-1)
    }
  }

  const handleToggledClicked = (e) => {
    setShowHistory(!showHistory)
  }

  const loadData = async () => {
    const orderValidationResponse = await fetchOrder({
      lang,
      cdnumdoc: cdnumdoc,
    })
    setOrder(orderValidationResponse?.data)
  }

  /**
   * Reload order to validate and show its associated Pdf order
   */
  const reloadData = () => {
    const url = encodeURI(
      window.location.href.split('/orders')[0] + UrlApis.URL_ORDER_PDF_GET + '?lang=' + lang + '&cdnumdoc=' + cdnumdoc,
    )
    setUrlPdf(url)
    // Enforce a reloading of the PDF (with signatures eventually)
    setUrlPdfKeyNum(urlPdfKeyNum + 1)

    loadData()
  }

  useEffect(() => {
    reloadData()
  }, [lang])

  useEffect(() => {
    setIsUserActionCompleted(false)

    if (order) {
      // Setting various permissions (view CA/CL/CR/CS, edit, sign)
      const _canView =
        (order.type === 'CA' &&
          LocalStorage.getOrDefault(
            [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.ORDERS_STCA_PERMISSIONS],
            LocalStorage.VALUE_TYPE.BOOLEAN,
            false,
          )) ||
        (order.type === 'CA_AOG' &&
          LocalStorage.getOrDefault(
            [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.ORDERS_STCA_AOG_PERMISSIONS],
            LocalStorage.VALUE_TYPE.BOOLEAN,
            false,
          )) ||
        (order.type === 'CL' &&
          LocalStorage.getOrDefault(
            [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.ORDERS_STCL_PERMISSIONS],
            LocalStorage.VALUE_TYPE.BOOLEAN,
            false,
          )) ||
        (order.type === 'CR' &&
          LocalStorage.getOrDefault(
            [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.ORDERS_STCR_PERMISSIONS],
            LocalStorage.VALUE_TYPE.BOOLEAN,
            false,
          )) ||
        (order.type === 'CS' &&
          LocalStorage.getOrDefault(
            [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.ORDERS_STCS_PERMISSIONS],
            LocalStorage.VALUE_TYPE.BOOLEAN,
            false,
          ))
      setCanView(_canView)

      const _canEdit = order.writeable
      setCanEdit(_canEdit)

      const loggedUser = LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined)
      const _isUserResponsible = loggedUser.toLowerCase() === order.responsible['userCode'].toLowerCase()
      setIsUserResponsible(_isUserResponsible)
      let _userNivVal = undefined
      switch (order.type) {
        case 'CA':
          _userNivVal = LocalStorage.getOrDefault(
            [LocalStorage.KEY_LEVELS, ComponentsUNames.ORDERS_STCA_LEVELS],
            LocalStorage.VALUE_TYPE.INTEGER,
            LEVEL_MIN_PERMISSION,
          )
          break
        case 'CA_AOG':
          _userNivVal = LocalStorage.getOrDefault(
            [LocalStorage.KEY_LEVELS, ComponentsUNames.ORDERS_STCA_AOG_LEVELS],
            LocalStorage.VALUE_TYPE.INTEGER,
            LEVEL_MIN_PERMISSION,
          )
          break
        case 'CL':
          _userNivVal = LocalStorage.getOrDefault(
            [LocalStorage.KEY_LEVELS, ComponentsUNames.ORDERS_STCL_LEVELS],
            LocalStorage.VALUE_TYPE.INTEGER,
            LEVEL_MIN_PERMISSION,
          )
          break
        case 'CR':
          _userNivVal = LocalStorage.getOrDefault(
            [LocalStorage.KEY_LEVELS, ComponentsUNames.ORDERS_STCR_LEVELS],
            LocalStorage.VALUE_TYPE.INTEGER,
            LEVEL_MIN_PERMISSION,
          )
          break
        case 'CS':
          _userNivVal = LocalStorage.getOrDefault(
            [LocalStorage.KEY_LEVELS, ComponentsUNames.ORDERS_STCS_LEVELS],
            LocalStorage.VALUE_TYPE.INTEGER,
            LEVEL_MIN_PERMISSION,
          )
          break
        default:
          break
      }
      setUserNivVal(_userNivVal)

      // Variables for the different states of this order
      const _isOrderSignedByResp = order.nivval !== ''
      setIsOrderSignedByResponsible(_isOrderSignedByResp)
      const _isOrderFinalized = _isOrderSignedByResp && order.nivval >= order.nivcde
      setIsOrderFinalized(_isOrderFinalized)
      setCanUserCompletlySign(_userNivVal >= parseInt(order.nivcde))

      // Decide whether a specific message has to be shown or not
      let _msgToDisplay = undefined
      if (_canEdit) {
        // User can validate
        if (_isOrderFinalized) {
          // Order has been signed and validated by someone holding the same validation level as logged User
          _msgToDisplay = new Message(MessageSeverity.INFO, '', null, t('9500149'))
        } else if (!_isOrderSignedByResp && !_isUserResponsible) {
          // Order not signed by responsible yet and current user is not the responsible for this order => The user can only view this order
          _msgToDisplay = new Message(MessageSeverity.INFO, '', null, t('9500156'))
        } else if (!_isOrderFinalized && _isOrderSignedByResp && _userNivVal < order.nivval) {
          // Validation level of user is below the minimum required validation level for the order
          _msgToDisplay = new Message(MessageSeverity.INFO, '', null, t('9500150'))
        }
      } else {
        // The user can only view this order
        _msgToDisplay = new Message(MessageSeverity.INFO, '', null, t('9500156'))
      }
      setMessageToDisplay(_msgToDisplay)
    }
  }, [order, isUserActionCompleted])

  useEffect(() => {
    if (isUserActionCompleted) {
      setIsUserActionCompleted(false)
    }
  }, [isUserActionCompleted])

  return (
    <>
      <PageHeader pageTitle={t('9500124')} navItems={navItems} />
      <Container style={{ flex: 1 }} until='xl'>
        {canView && order && (
          <>
            <Inline align='center' spacing='4-x'>
              <CustomNumericInput label={t('9500122')} value={order.nivval ? parseInt(order.nivval) : '-'} readOnly />
              <CustomNumericInput label={t('9500123')} value={parseInt(order.nivcde)} readOnly />
              {canViewPrice && <CustomInput label={t('7001202')} value={order?.totalHt} readOnly />}
            </Inline>
            <Inline align='center' spacing='4-x'>
              <CustomInput label={t('7000892')} value={order.responsible?.userName} readOnly />
              <CustomInput label={t('7001518')} value={order.lastSignature} readOnly />
              <FormControlLabel> </FormControlLabel>
              <Toggle name='order_show_history' tabIndex={0} checked={showHistory} onChange={handleToggledClicked} />
            </Inline>
            <div>{showHistory && <History data={order?.history} isOrderFinalized={isOrderFinalized} />}</div>
          </>
        )}

        <Stack>
          <div>
            {!canView && order && t('9501005')}
            {canView && order && messageToDisplay && <MessageBlock message={messageToDisplay} />}
          </div>
          {canView && urlPdf && (
            <embed
              key={'urlPdfKeyNum_' + urlPdfKeyNum}
              title='PDF order'
              src={urlPdf}
              style={{ width: '100%', height: '100vh' }}
            />
          )}
        </Stack>

        {order &&
          canView &&
          canEdit &&
          userNivVal !== undefined &&
          !isOrderFinalized &&
          ((!isOrderSignedByResponsible && isUserResponsible) ||
            (isOrderSignedByResponsible && userNivVal > parseInt(order.nivval))) && (
            <Inline align='space-around'>
              {order && !isOrderFinalized && (
                <Refusal
                  lang={lang}
                  idCommand={order.cdnumdoc}
                  actionName={ACTION_PERFORMED.REFUSAL}
                  onUserActionCompleted={handleUserActionCompletion}
                />
              )}
              {order && !isOrderSignedByResponsible && isUserResponsible && (
                <Validation
                  lang={lang}
                  actionName={t('9500136')}
                  idCommand={order.cdnumdoc}
                  actionType={ACTION_PERFORMED.VALIDATION}
                  canUserCompletlySign={canUserCompletlySign}
                  onUserActionCompleted={handleUserActionCompletion}
                />
              )}
              {order && !isOrderFinalized && isOrderSignedByResponsible && !canUserCompletlySign && (
                <Validation
                  lang={lang}
                  actionName={t('8000202')}
                  idCommand={order.cdnumdoc}
                  actionType={ACTION_PERFORMED.VALIDATION}
                  canUserCompletlySign={canUserCompletlySign}
                  onUserActionCompleted={handleUserActionCompletion}
                />
              )}
              {order && !isOrderFinalized && isOrderSignedByResponsible && canUserCompletlySign && (
                <Validation
                  lang={lang}
                  actionName={t('8000202')}
                  idCommand={order.cdnumdoc}
                  actionType={ACTION_PERFORMED.VALIDATION}
                  canUserCompletlySign={canUserCompletlySign}
                  onUserActionCompleted={handleUserActionCompletion}
                />
              )}
            </Inline>
          )}
      </Container>
    </>
  )
}

export default OrderValidationPage
